import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import Form from "@/components/Form";
import { QueryTable } from "@/components/query-table/QueryTable";
import SaveButton from "@/components/SaveButton";
import { Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import useQueryData from "@/hooks/useQueryData";
import { useSyncFormWithQueryData } from "@/hooks/useSyncFormWithQueryData";
import { FilterType, mergeFilters } from "@/lib/filters";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "@/modules/projects/hooks/useCheckIsProjectLocked";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { readDepartmentBasedAssignments } from "@/schemas/departmentBasedAssignments";
import { readRoleBasedAssignments } from "@/schemas/roleBasedAssignments";
import { FetchListRequest } from "@/services/api";
import { zodResolver } from "@hookform/resolvers/zod";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useFormBaseline } from "@/hooks/useFormBaseline";
import { useFormDefaultValuesFromQuery } from "@/hooks/useFormDefaultValuesFromQuery";
import { useUpdateContractorsAssignmentMutation } from "../hooks/useUpdateContractorsAssignmentMutation";
import {
  departmentBasedAssignmentsFormSchema,
  roleBasedAssignmentsFormSchema,
} from "../schema";
import { AssigneesForm, ReadContractorAssignee } from "../types";
import { ContractorsAssignmentsAddButton } from "./ContractorsAssignmentsAddButton";
import { useContractorsAssignmentsColumnOverrides } from "./ContractorsAssignmentsColumnOverrides";
import { useContractorsAssignmentsContext } from "./ContractorsAssignmentsContext";
import { ProjectDetailsAndNavigation } from "../../ProjectDetailsAndNavigation";
import { MenubarContainer } from "@/components/MenubarContainer";

const defaultValues = {};

export const ContractorsAssignmentsForm = () => {
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const {
    isRoleBasedProject,
    setAddContractorDialogOpen,
    ProjectContractorsService,
    projectContractorsMainQueryKey,
  } = useContractorsAssignmentsContext();

  const projectId = useProjectId();

  const schema = useMemo(
    () =>
      isRoleBasedProject
        ? roleBasedAssignmentsFormSchema
        : departmentBasedAssignmentsFormSchema,
    [isRoleBasedProject],
  );

  const form = useForm<AssigneesForm>({
    resolver: zodResolver(schema),
    defaultValues,
    disabled: !can_update || isProjectLocked,
  });

  const options = useCreateDataTableOptions<ReadContractorAssignee>(
    projectContractorsMainQueryKey,
  );

  const filteredOptions = useMemo(() => {
    if (isRoleBasedProject && projectId) {
      return {
        ...options,
        filters: mergeFilters(options.filters, [
          {
            id: "project_id",
            value: {
              filter: FilterType.EqualsString,
              value: projectId,
            },
          },
        ]),
      };
    }

    return options;
  }, [isRoleBasedProject, options, projectId]);

  const queryKey = useDynamicQueryParams(
    [projectContractorsMainQueryKey, projectId],
    filteredOptions,
  );

  const fetchList = useCallback(
    (fetchParams: FetchListRequest<ReadContractorAssignee>) =>
      ProjectContractorsService.getList(fetchParams),
    [ProjectContractorsService],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchList({
        lastItem: pageParam,
        filters: filteredOptions.filters,
        sorting: filteredOptions.sorting[0],
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const data = useQueryData(query);
  const formDefaultValues = useFormDefaultValuesFromQuery<
    ReadContractorAssignee,
    "entries",
    AssigneesForm
  >(data);

  const { baseline, updateBaseline } = useFormBaseline(form, formDefaultValues);

  useSyncFormWithQueryData(data, form);

  const contractorsAssignmentsUpdateMutation =
    useUpdateContractorsAssignmentMutation();

  const handleSubmit: SubmitHandler<AssigneesForm> = useCallback(
    async (data) => {
      const changedFields = Object.entries(data.entries)
        .filter(([key]) => {
          const state = form.getFieldState(`entries.${key}`);
          return state.isDirty;
        })
        .map(([, field]) => field);

      if (changedFields.length === 0) {
        return;
      }

      await contractorsAssignmentsUpdateMutation.mutateAsync(changedFields);
      updateBaseline();
    },
    [form, contractorsAssignmentsUpdateMutation, updateBaseline],
  );

  const onAddCreator = useCallback(() => {
    setAddContractorDialogOpen(true);
  }, [setAddContractorDialogOpen]);

  const columnOverrides = useContractorsAssignmentsColumnOverrides();

  const readSchema = useMemo(
    () =>
      isRoleBasedProject
        ? readRoleBasedAssignments
        : readDepartmentBasedAssignments,
    [isRoleBasedProject],
  );

  const context = useMemo(() => ({ query }), [query]);

  const tableFeatures = useMemo(
    () => ({
      editionHighlight: { baseline },
      exportSettings: {
        moduleName: isRoleBasedProject
          ? Module.RoleBasedAssignments
          : Module.DepartmentBasedAssignments,
      },
    }),
    [baseline, isRoleBasedProject],
  );

  return (
    <div className="flex flex-col gap-2">
      <ProjectDetailsAndNavigation />
      <DataTableQueryContextProvider value={context}>
        <QueryTable
          schema={readSchema}
          name={projectContractorsMainQueryKey}
          query={query}
          options={filteredOptions}
          columnOverrides={columnOverrides}
          translationKey="contractorsAssignments"
          tableFeatures={tableFeatures}
        >
          <MenubarContainer>
            <QueryTable.Menubar className="mb-0 grow">
              <QueryTable.Menubar.Tools />
              <QueryTable.Menubar.Columns />
              <QueryTable.Menubar.Settings />
            </QueryTable.Menubar>
            <ContractorsAssignmentsAddButton onClick={onAddCreator} />
          </MenubarContainer>
          <Form form={form} handleSubmit={handleSubmit}>
            <QueryTable.Content>
              <QueryTable.Status>
                <ContractorsAssignmentsAddButton onClick={onAddCreator} />
                <QueryTable.DirtyRowsStatus />
              </QueryTable.Status>
            </QueryTable.Content>
            <footer className="flex p-2 justify-end gap-6">
              <SaveButton />
            </footer>
          </Form>
        </QueryTable>
      </DataTableQueryContextProvider>
    </div>
  );
};
